import { faFilter, faInfoCircle, faLongArrowDown as faLongArrowDownLight, faLongArrowUp as faLongArrowUpLight } from '@fortawesome/pro-light-svg-icons'
import { faLongArrowDown as faLongArrowDownSolid, faLongArrowUp as faLongArrowUpSolid } from '@fortawesome/pro-solid-svg-icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TableCellProps } from 'react-virtualized'
import { getMinPxFromElement } from '../../../../utils/globals/components'
import useOnClickOutside from '../../../../utils/hooks/useOnClickOut'
import LoadingSpinner from '../../../LoaderDecider/LoadingSpinner'
import SearchBar from '../../../SearchBar'
import TwinIcon, { TwinIconProp, TwinIconToolTip } from '../../../TwinIcon'
import '../../table.sass'
import { HeaderChangesTy, Order, TableConfiguration } from '../../types'
import { ChangeColumnConfigTy } from '../types'
import ModalFilter, { SingleElementModalFilterTableRenderer } from './ModalFilter'
import { AllSearchableComponents, CustomRender, HeaderColumnProps, SearchableComponentProps } from './types'
import { parseDefaultColumnSelectedFilterValue } from './functions'
import useMyDebounce, { MyDebounceProps } from '../../../../utils/hooks/useMyDebounce'
import { Modify } from '../../../../utils/globals/types'

type HeaderRenderProps = HeaderRenderLogicProps & HeaderColumnProps & {
    tableConfiguration?: TableConfiguration
    headerChanges?: HeaderChangesTy
    headerHeight?: number
    columnSelectedOrder?: Order
    columnSelectedFilter?: any
}
export const HeaderRender: React.FC<HeaderRenderProps> = ({ label, toolTip, dataKey, sortKey, headerHeight, sortable, searchableComponent, tableConfiguration, width, headerChanges, columnSelectedOrder, columnSelectedFilter, updateEmployeeParameter = true, id, ...rest }) => {
    const { setRef } = useHeaderRenderLogic({ id, ...rest })
    const filterValue = parseDefaultColumnSelectedFilterValue(columnSelectedFilter?.value)
    return (
        <div className='flex justify-start items-center medium14 bg-gray-F7' key={dataKey}>
            <div className='flex items-center w-full'
                style={{ height: headerHeight }}>
                {!label || typeof label === 'string' ? label : label({ value: filterValue, selectedFilterValue: filterValue })}
                {!toolTip ? '' : typeof toolTip === 'string' ? <TwinIconToolTip positionStrategy='fixed' className='ml-6' icon={faInfoCircle} message={toolTip} id={id+'tooltip'} classNameTooltip='max-w-300' /> : toolTip({})}
                <HeaderSortable {...{ dataKey: id, sortable, changeOrder: headerChanges?.changeOrder, columnSelectedOrder }} />
                <HeaderSearchable {...{ searchableComponent, dataKey: id, value: filterValue }} changeSingleField={headerChanges?.changeSingleField} />
                {!width && updateEmployeeParameter ? <div className='table_header_separator cursor-col-resize' ref={setRef}></div> : null}
            </div>
        </div>
    )
}

interface HeaderRenderLogicProps  {
    changeColumnConfig: ChangeColumnConfigTy
    defaultMinWidthColumns: number
    minWidth?: HeaderColumnProps['minWidth']
    id: HeaderColumnProps['id']
    updateEmployeeParameter?: boolean
}

const useHeaderRenderLogic = ({ minWidth, defaultMinWidthColumns, changeColumnConfig, id, updateEmployeeParameter }: HeaderRenderLogicProps) => {
    const [ref, setRef] = useState<any | null>(null)
    useEffect(() => {
        let movement = { starterPx: 0, starterWidth: 0 }
        const mouseUp = (event: any) => {
            document.querySelector('body')?.classList.remove('cursor-col-resize')
            document.removeEventListener('mousemove', mouseMove)
            let myWidth = movement.starterWidth + (event.clientX - movement.starterPx)
            const myMinWidth = minWidth || defaultMinWidthColumns
            if (myWidth < myMinWidth) {
                myWidth = myMinWidth
            }
            changeColumnConfig(id, { userWidth: myWidth }, updateEmployeeParameter)
            document.removeEventListener('mouseup', mouseUp)
        }
        const mouseMove = (event: any) => {
            const headerElement = ref?.parentElement?.parentElement?.parentElement
            if (headerElement) {
                let myWidth = movement.starterWidth + (event.clientX - movement.starterPx)
                const myMinWidth = minWidth || defaultMinWidthColumns
                if (myWidth < myMinWidth) {
                    myWidth = myMinWidth
                }
                changeColumnConfig(id, { userWidth: myWidth }, false)
            }
        }
        const mouseDown = (event: any) => {
            const target = event.target
            const headerElement = target?.parentElement?.parentElement?.parentElement
            if (headerElement && headerElement.classList.contains('ReactVirtualized__Table__headerColumn')) {
                movement = { starterPx: event.clientX, starterWidth: getMinPxFromElement(headerElement) }
            }
            document.querySelector('body')?.classList.add('cursor-col-resize')
            document.addEventListener('mousemove', mouseMove)
            document.addEventListener('mouseup', mouseUp)
        };
        if (ref) {
            ref.addEventListener("mousedown", mouseDown);
        }
        return () => {
            if (ref) {
                ref.removeEventListener("mousedown", mouseDown);
                document.removeEventListener('mousemove', mouseMove)
                document.removeEventListener('mouseup', mouseUp)
            }
        };
    }, [ref, id, minWidth, changeColumnConfig, defaultMinWidthColumns, updateEmployeeParameter])
    
    return { ref, setRef }
}

interface HeaderSearchableProps extends SearchableComponentProps {
    searchableComponent?: AllSearchableComponents
    changeSingleField?: (fieldName: string, value: string | string[]) => void
}

export const HeaderSearchable: React.FC<HeaderSearchableProps> = ({ searchableComponent, changeSingleField, ...rest }) => {
    const { opened, setOpened, ref, getOffsetWidth } = useHeaderSearchableLogic()
    if (!searchableComponent) {
        return null
    }
    return (
        <div ref={ref}>
            <TwinIcon className={'ml-12 cursor-pointer ' + (opened || rest.value ? 'text-green-43' : 'text-gray-B2')} icon={faFilter} onClick={() => setOpened((oldOpened) => !oldOpened)}/>
            {(opened && searchableComponent) && <ModalFilter {...{ opened, setOpened }} style={getOffsetWidth()} changeSingleField={changeSingleField} dataKey={rest.dataKey}><SingleElementModalFilterTableRenderer {...{ component: searchableComponent.component, opened, setOpened, ...rest }} changeSingleField={changeSingleField}extraComponentData={searchableComponent.extraComponentData} /></ModalFilter>}
        </div>
    )
}
const useHeaderSearchableLogic = () => {
    const [opened, setOpened] = useState<boolean>(false)
    const ref = useRef<any>(null)
    const current = ref.current
    const getOffsetWidth = useCallback(() => {
        if (current) {
            const offsets = current.parentElement.getBoundingClientRect()
            let posParams: any= {
                left: offsets.left + 'px'
            }
            if ((200 + offsets.right) > window.innerWidth && offsets.left > 200){
                posParams = {
                    right: (window.innerWidth - offsets.right) + 'px'
                }
            }
            return { top: offsets.top + 40 + 'px', ...posParams }
        }
        return {top: undefined, left: undefined}
    }, [current])
    useOnClickOutside(ref, () => setTimeout(() => setOpened(false), 100))
    return { opened, setOpened, ref, getOffsetWidth }
}

interface HeaderSortableProps {
    dataKey: string
    changeOrder?: (fieldName: string) => void
    sortable?: boolean
    columnSelectedOrder?: Order
}

const HeaderSortable: React.FC<HeaderSortableProps> = ({ dataKey, changeOrder, columnSelectedOrder, sortable = false }) => {
    if (!sortable) {
        return null
    }
    return (
        <div className='flex justify-center items-center' onClick={() => changeOrder?.(dataKey)}>
            <HeaderSortIcon order='asc' icon={faLongArrowDownLight} solidIcon={faLongArrowDownSolid} {...{ dataKey,columnSelectedOrder }}/>
            <HeaderSortIcon order='desc' icon={faLongArrowUpLight} solidIcon={faLongArrowUpSolid} {...{ dataKey, columnSelectedOrder }} />
        </div>
    )
}
interface HeaderSortIconProps {
    icon: TwinIconProp
    solidIcon: TwinIconProp
    order: 'asc' | 'desc'
    columnSelectedOrder?: Order
}

const HeaderSortIcon: React.FC<HeaderSortIconProps> = ({ icon, solidIcon, order, columnSelectedOrder }) => {
    let className = (order === 'asc' ? 'ml-12' : 'ml-2') + ' cursor-pointer'
    let myIcon = icon
    if (columnSelectedOrder === order) {
        // @ts-ignore
        className += ' text-green-43'
        myIcon = solidIcon
    } else {
        className += ' text-gray-B2'
    }
    return (
        <TwinIcon className={className} icon={myIcon} size='sm' />
    )
}
    
interface CellRenderProps extends CellRenderLogicProps {
    rowHeight?: number
    isNullTable?: boolean
}

export const CellRender: React.FC<CellRenderProps> = ({ columnIndex, rowHeight = 48, isNullTable, ...rest }) => {
    const { result } = useCellRenderLogic({ ...rest, columnIndex })
    if (isNullTable) {
        return <CellRenderNullTable columnIndex={columnIndex} key={columnIndex}></CellRenderNullTable>
    }
    return (
        <div key={columnIndex} className='flex items-center justify-start' style={{ height: rowHeight }}>
            {result}
        </div>
    );
}
interface CellRenderLogicProps extends TableCellProps  {
    parseData?: (value: any) => void
    customRender?: CustomRender
    myColumnWidth: number
}

const useCellRenderLogic = ({ cellData, parseData, rowData, customRender, rowIndex, myColumnWidth }: CellRenderLogicProps) => {
    let result = cellData
    if (parseData) {
        result  = parseData?.(cellData)
    }
    if (customRender) {
        result = customRender(result, { ...rowData, rowIndex, _myColumnWidth: myColumnWidth })
    } else {
        result = <span className='twin_elipsis_table_two_lines'>{result}</span>
    }
    return {result}
}

interface CellRenderNullTableProps {
    columnIndex: number
}

const CellRenderNullTable: React.FC<CellRenderNullTableProps> = ({ columnIndex }) => {
    if (columnIndex === 0) {
        return (
            <div key={columnIndex} className='cursor-pointer  flex items-center justify-start tablecellnull'>
                <LoadingSpinner/>
            </div>
        )
    }
    return null
}

export type SearchBarTableProps = Modify<MyDebounceProps, {
    value?: string
    placeholder: string
    className?: string
}>

export const SearchBarTable: React.FC<SearchBarTableProps> = ({ placeholder, className, value = '',  ...logic }) => {
    const { myValue, changeMyValue, ref, myOnBlur } = useMyDebounce({ ...logic, value, timeDebounce: 300})
    return (
        <SearchBar myref={ref} className={'search_bar_table ' + (className || '')} placeholder={placeholder} searchValue={myValue} onChange={(value) => changeMyValue(value)} onBlur={myOnBlur} />
    )
}